<script setup lang="ts">
/**
 * NotificationBell - top-bar bell that surfaces the number of signals visible to
 * the current user. Activating it opens the signaleringen page, which lists those
 * same signals in full (#3170) — previously it dropped the user on the dashboard,
 * where only a capped org-wide slice is shown.
 *
 * Part of the UI refresh (#2749): the identity cluster + notifications moved from
 * the sidebar into the top-right header.
 */
import { onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { Bell } from 'lucide-vue-next'
import { useSignals } from '@/composables/useSignals'
import SignalBadge from '@/components/atoms/SignalBadge.vue'

const { t } = useI18n()
const router = useRouter()
const { visibleToMeCount, startVisibleToMeCountPolling, stopVisibleToMeCountPolling } = useSignals()

// The top bar stays mounted for the whole session, so a one-off fetch would
// never change. TopBar renders the bell only with an organisation, so logging
// out unmounts it and stops the poll.
onMounted(startVisibleToMeCountPolling)
onUnmounted(stopVisibleToMeCountPolling)

function openNotifications() {
  router.push({ name: 'signals' })
}
</script>

<template>
  <button
    type="button"
    class="header-action notification-bell"
    :aria-label="t('notifications.open', { count: visibleToMeCount })"
    :title="t('notifications.open', { count: visibleToMeCount })"
    data-testid="notification-bell"
    @click="openNotifications"
  >
    <Bell class="notification-bell__icon" :size="20" aria-hidden="true" />
    <SignalBadge
      v-if="visibleToMeCount > 0"
      :count="visibleToMeCount"
      class="notification-bell__badge"
      data-testid="notification-bell-badge"
    />
  </button>
</template>

<!-- The 44x44 outlined box, shared with the rest of the header row (#4080). -->
<style src="@/assets/styles/header-action.css"></style>

<style scoped>
/* The badge is placed against the button's corners, so the button is its origin.
   It straddles the top edge so it never reaches down over the icon; `translate`
   rather than `transform`, which the badge's own pulse animation owns. */
.notification-bell {
  position: relative;
}

.notification-bell__icon {
  display: block;
}

.notification-bell__badge {
  position: absolute;
  top: 0;
  right: -6px;
  translate: 0 -50%;
}
</style>
